/**
 * Materials for one level.
 *
 * `MeshStandardMaterial` throughout. Lambert is cheaper, but the floor needs a
 * normal map lit by a moving flashlight cone and the tile/metal levels need a
 * roughness response — both are what stop the surfaces reading as flat colour,
 * and both are exactly what Lambert throws away. The maps already carry the
 * palette, so `color` stays white and nothing is tinted twice.
 *
 * Texture repeats are set here rather than in `textures.ts` so the metres-per-
 * tile decision lives next to the level scale. UVs are assumed to be in metres
 * (the geometry is generated from world coordinates), so one repeat per metre
 * means `repeat = 1 / TILE_METRES`.
 */

import * as THREE from 'three';
import { clamp01 } from '../core/math';
import type { LevelSpec } from '../core/types';
import type { MaterialSet, TextureSet } from './api';
import { TILE_METRES } from './textures';

/** Emissive punch of a lit fluorescent panel before the flicker envelope. */
const PANEL_EMISSIVE = 1.35;

/** Metres per second the caustic map drifts across the water plane. */
const WATER_DRIFT_U = 0.021;
const WATER_DRIFT_V = 0.013;

interface SurfaceResponse {
  wallRoughness: number;
  wallMetalness: number;
  floorRoughness: number;
  floorMetalness: number;
  normalScale: number;
}

/** How each level's surfaces answer a light. Concrete drinks it, tile throws it back. */
function responseFor(spec: LevelSpec): SurfaceResponse {
  switch (spec.generator) {
    case 'warehouse':
    case 'lightsout':
    case 'city':
      return { wallRoughness: 0.94, wallMetalness: 0, floorRoughness: 0.88, floorMetalness: 0, normalScale: 0.6 };
    case 'pipes':
      return { wallRoughness: 0.62, wallMetalness: 0.3, floorRoughness: 0.85, floorMetalness: 0.05, normalScale: 0.7 };
    // Brick and dusty concrete: no metal anywhere, and rougher than the panel
    // work it used to borrow. This is what stops Level 3 catching the torch
    // like a plant room.
    case 'station':
      return { wallRoughness: 0.93, wallMetalness: 0, floorRoughness: 0.9, floorMetalness: 0, normalScale: 0.75 };
    case 'poolrooms':
      return { wallRoughness: 0.24, wallMetalness: 0, floorRoughness: 0.3, floorMetalness: 0, normalScale: 0.45 };
    case 'offices':
    case 'hotel':
    default:
      return { wallRoughness: 0.97, wallMetalness: 0, floorRoughness: 0.98, floorMetalness: 0, normalScale: 0.85 };
  }
}

function setRepeat(tex: THREE.Texture | null, metres: number, vMetres = metres): void {
  if (!tex) return;
  tex.repeat.set(1 / metres, 1 / vMetres);
}

export function makeMaterialSet(spec: LevelSpec, textures: TextureSet): MaterialSet {
  const pal = spec.palette;
  const res = responseFor(spec);

  setRepeat(textures.wall, TILE_METRES.wall);
  // The trim map is the bottom metre of a wall and its V is clamped, so it must
  // not repeat vertically however tall the renderer makes the band.
  setRepeat(textures.wallTrim, TILE_METRES.trim, 1);
  setRepeat(textures.floor, TILE_METRES.floor);
  setRepeat(textures.floorNormal, TILE_METRES.floor);
  setRepeat(textures.ceiling, TILE_METRES.ceiling);
  setRepeat(textures.lightPanel, TILE_METRES.light);
  setRepeat(textures.water, TILE_METRES.water);

  const made: THREE.Material[] = [];
  const track = <T extends THREE.Material>(m: T): T => {
    made.push(m);
    return m;
  };

  const wall = track(
    new THREE.MeshStandardMaterial({
      map: textures.wall,
      roughness: res.wallRoughness,
      metalness: res.wallMetalness,
    })
  );

  const trim = track(
    new THREE.MeshStandardMaterial({
      map: textures.wallTrim,
      roughness: Math.min(0.99, res.wallRoughness + 0.02),
      metalness: res.wallMetalness,
    })
  );

  const floorMat = new THREE.MeshStandardMaterial({
    map: textures.floor,
    roughness: res.floorRoughness,
    metalness: res.floorMetalness,
  });
  if (textures.floorNormal) {
    floorMat.normalMap = textures.floorNormal;
    floorMat.normalScale.set(res.normalScale, res.normalScale);
  }
  const floor = track(floorMat);

  const ceiling = track(
    new THREE.MeshStandardMaterial({
      map: textures.ceiling,
      roughness: 0.95,
      metalness: 0,
    })
  );

  // Pillars share the wall map but sit a shade darker, so they read as volume
  // rather than as a wall that has wandered into the room.
  const pillar = track(
    new THREE.MeshStandardMaterial({
      map: textures.wall,
      color: 0xdad6d0,
      roughness: res.wallRoughness,
      metalness: res.wallMetalness,
    })
  );

  const lightPanel = track(
    new THREE.MeshStandardMaterial({
      map: textures.lightPanel,
      emissiveMap: textures.lightPanel,
      emissive: new THREE.Color(pal.lightTint),
      emissiveIntensity: PANEL_EMISSIVE,
      roughness: 0.55,
      metalness: 0,
    })
  );

  const lightPanelDead = track(
    new THREE.MeshStandardMaterial({
      map: textures.lightPanel,
      color: 0x4a4a44,
      roughness: 0.7,
      metalness: 0,
    })
  );

  const water = textures.water
    ? track(
        new THREE.MeshStandardMaterial({
          map: textures.water,
          transparent: true,
          opacity: 0.82,
          roughness: 0.07,
          metalness: 0.02,
          side: THREE.DoubleSide,
          // Wading through it, the player is often inside the plane's depth
          // range; writing depth makes the far side of the room pop.
          depthWrite: false,
        })
      )
    : null;

  const doorframe = track(
    new THREE.MeshStandardMaterial({
      // Painted casing, not stained hardwood. Derived from the wallpaper rather
      // than from the skirting so the frame reads as the same yellow as the
      // room with a little more light on it — a brown frame makes the place
      // look like an office, which is exactly what it must not look like.
      color: new THREE.Color(pal.wall).multiplyScalar(1.12),
      roughness: 0.66,
      metalness: 0,
    })
  );

  const waterMap = textures.water;

  return {
    wall,
    trim,
    floor,
    ceiling,
    pillar,
    lightPanel,
    lightPanelDead,
    water,
    doorframe,

    update(dt: number, time: number, flicker: number): void {
      // Mains hum in the tubes: a shallow, fast breath under whatever envelope
      // the light system is applying. Everything else is static on purpose —
      // per-frame writes here would upload uniforms for the whole level.
      const mains = 1 + Math.sin(time * 21.3) * 0.02 + Math.sin(time * 7.1) * 0.012;
      lightPanel.emissiveIntensity = PANEL_EMISSIVE * clamp01(flicker) * mains;

      if (waterMap && water) {
        // Wrapped, or the offset loses precision over a long run.
        waterMap.offset.x = (waterMap.offset.x + WATER_DRIFT_U * dt) % 1;
        waterMap.offset.y = (waterMap.offset.y + WATER_DRIFT_V * dt) % 1;
        water.opacity = 0.78 + Math.sin(time * 0.37) * 0.04;
      }
    },

    dispose(): void {
      // Textures belong to the TextureSet that made them; only materials are
      // this set's to free.
      for (const m of made) m.dispose();
      made.length = 0;
    },
  };
}
